import { AfterViewInit, Component, OnInit, inject, signal, input, viewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { merge, of as observableOf } from 'rxjs';
import { catchError, startWith, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { TransactionDetailsPopupComponent } from '../../popups/transaction-details-popup/transaction-details-popup.component';
import { PanoptycDataService } from '../../../service/panoptyc-data.service';
import { ImagePopupComponent, ImagePopupDialogData } from '../../popups/image-popup/image-popup.component';
import { UserLoginService } from '../../../service/user-login.service';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { IdentityLocationFilter, MissingFace } from '../../../types';
import { NotifyService } from '../../../service/notify.service';
import { IdentitiesService } from '../../../service/identitites.service';

@Component({
  selector: 'app-uncertain-faces',
  templateUrl: './uncertain-faces.component.html',
  styleUrls: ['./uncertain-faces.component.scss'],
  standalone: false,
})
export class UncertainFacesComponent implements AfterViewInit, OnInit {
  public userService = inject(UserLoginService);
  public router = inject(Router);
  private dataService = inject(PanoptycDataService);
  private identitiesService = inject(IdentitiesService);
  private dialog = inject(MatDialog);
  private notifyService = inject(NotifyService);

  public session = input<any>();
  public isAdmin = input<boolean>(false);

  public loadingPageData = signal<boolean>(false);
  public selectedLocations = signal<string[]>([]);
  public numberOfResults = signal<number>(0);

  selectedImageUrl = '';

  displayedColumns: string[] = [];
  readonly displayedColumnsStandard: string[] = [
    'details',
    'img_id',
    'theft',
    'location_name',
    'branch',
    'total',
    'theft_category',
    'cancel_date',
  ];
  readonly displayedColumnsAdmin: string[] = [
    'details',
    'img_id',
    'theft',
    'location_name',
    'branch',
    'total',
    'theft_category',
    'cancel_date',
  ];

  suspectIdentities: MissingFace[] = [];

  filterBoxExpanded = false;

  highlightApply = false;
  loadingData = false;

  minDate: Date;
  maxDate: Date = DateTime.now().toJSDate();

  allLocations = signal<IdentityLocationFilter[]>([]);

  dateFrom = signal<Date | undefined>(DateTime.now().minus({ days: 7 }).toJSDate());
  dateTo = signal<Date | undefined>(DateTime.now().toJSDate());
  theftRisk = 'all';

  readonly paginator = viewChild(MatPaginator);
  readonly sort = viewChild(MatSort);

  togglePanel() {
    this.filterBoxExpanded = !this.filterBoxExpanded;
  }

  changeLocations() {
    this.highlightApply = true;
  }

  handleDataForbidden(error) {
    if (error.status === 403) {
      try {
        this.router.navigate(['/403']);
      } catch (e) {
        console.error(e);
      }
    }
  }

  showTransactionDetailsPopup(clipUrl, row): void {
    const dialogRef = this.dialog.open(TransactionDetailsPopupComponent, {
      maxWidth: '95vw',
      maxHeight: '95vh',
      height: '95%',
      width: '95%',
      disableClose: true,
      panelClass: 'custom-dialog-container',
      data: {
        cancellationId: row.img_id,
        cancelDate: row.cancel_date,
        locationName: row.location_name,
        isTheft: row.is_theft,
        hasComments: row.num_comments > 0,
        hasVideo: row.num_videos > 0,
        hasDetails: true,
        session: this.session(),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      if ('remove' in result && result.remove === true) {
        this.removeImageFromIdentity();
      }
      row.is_theft = result.isTheft;
      row.theftLabel = this.getTheftLabel(row.is_theft);
      row.last_seen = result.lastSeen;
      row.num_videos = result.hasVideo ? 1 : 0;
      row.num_comments = result.hasComments ? 1 : 0;
    });

    dialogRef.backdropClick().subscribe(() => {
      dialogRef.close({
        cancellationId: dialogRef.componentInstance.cancellationId,
        lastSeen: dialogRef.componentInstance.lastSeen,
        isTheft: dialogRef.componentInstance.theftToValue(dialogRef.componentInstance.isTheft),
        hasVideo: dialogRef.componentInstance.hasVideo,
        hasComments: dialogRef.componentInstance.hasComments,
      });
    });

    dialogRef.keydownEvents().subscribe((event) => {
      if (event.key === 'Escape') {
        dialogRef.close({
          cancellationId: dialogRef.componentInstance.cancellationId,
          lastSeen: dialogRef.componentInstance.lastSeen,
          isTheft: dialogRef.componentInstance.theftToValue(dialogRef.componentInstance.isTheft),
          hasVideo: dialogRef.componentInstance.hasVideo,
          hasComments: dialogRef.componentInstance.hasComments,
        });
      }
    });
  }

  showImagePopup(row): void {
    this.dialog.open<ImagePopupDialogData, ImagePopupDialogData>(ImagePopupComponent, {
      width: '560px',
      data: {
        imageKey: row.img_id,
        // session: this.session,
      },
    });
  }

  ngOnInit() {
    // maxDate minus 30 days
    this.minDate = new Date(this.maxDate);
    this.minDate.setDate(this.minDate.getDate() - 30);

    this.displayedColumns = this.isAdmin() ? this.displayedColumnsAdmin : this.displayedColumnsStandard;

    this.loadingPageData.set(true);
    this.identitiesService.getIdentitiesFilterParams().subscribe(
      (data) => {
        this.allLocations.set(data.locations);

        this.loadingPageData.set(false);
      },
      (error) => {
        this.handleDataForbidden(error);
      },
    );
  }

  getMissedFaces() {
    this.loadingData = true;
    const paginator = this.paginator();
    paginator.pageIndex = 0;
    this.dataService.getMissingFaces(this.sort().active, this.sort().direction, paginator.pageIndex, this.getExtraParams()).subscribe(
      (data) => {
        this.loadingData = false;
        this.highlightApply = false;
        this.suspectIdentities = data;
        this.suspectIdentities.forEach((d) => (d['theftLabel'] = this.getTheftLabel(d['is_theft'])));
        this.numberOfResults.set(data.length);
      },
      (error) => {
        this.handleDataForbidden(error);
      },
    );
  }

  ngAfterViewInit() {
    // If the user changes the sort order, reset back to the first page.
    this.sort().sortChange.subscribe(() => (this.paginator().pageIndex = 0));

    merge(this.sort().sortChange, this.paginator().page)
      .pipe(
        startWith({}),
        switchMap(() => {
          return this.dataService!.getMissingFaces(
            this.sort().active,
            this.sort().direction,
            this.paginator().pageIndex,
            this.getExtraParams(),
          );
        }),
        catchError((error) => {
          this.handleDataForbidden(error);
          return observableOf([]);
        }),
      )
      .subscribe((data) => {
        this.suspectIdentities = data;
        this.suspectIdentities.forEach((d) => (d['theftLabel'] = this.getTheftLabel(d['is_theft'])));
      });
  }

  removeImageFromIdentity() {
    return;
  }

  theftToValue(isTheft) {
    if (isTheft === true) {
      return 1;
    }
    if (isTheft === false) {
      return 0;
    }
    return isTheft;
  }

  getTheftLabel(isTheft) {
    if (isTheft === 1) {
      return 'Theft';
    } else if (isTheft === 0) {
      return 'No theft';
    } else {
      return 'Click to review';
    }
  }

  handleTheftChange(isTheft, row) {
    let newCategory = isTheft === true ? 1 : 0;
    newCategory = isTheft === null ? 2 : newCategory;
    row.updatingCategory = true;

    this.dataService.updateTransactionCategory(row.img_id, newCategory).subscribe(
      () => {
        row.theftLabel = isTheft === true ? 'Theft' : 'No theft';
        row.theftLabel = isTheft === null ? 'Click to review' : row.theftLabel;
        row.is_theft = this.theftToValue(isTheft);
        row.updatingCategory = false;
        this.notifyService.notify('Successfully updated category');
      },
      (error) => {
        this.handleDataForbidden(error);
      },
    );
  }

  getExtraParams() {
    let locations = '';
    if (this.selectedLocations().length > 0) {
      locations = `location_ids=${this.selectedLocations().join('|')}`;
      locations = locations.replace(/#/g, '%23').replace(/&/g, '%26');
    }

    const cancelDateFrom = this.dateFrom()
      ? `cancel_date__gte=${DateTime.fromJSDate(this.dateFrom()).toFormat('yyyy-MM-dd')} 00:00:00`
      : null;
    const cancelDateTo = this.dateTo() ? `cancel_date__lte=${DateTime.fromJSDate(this.dateTo()).toFormat('yyyy-MM-dd')} 23:59:59` : null;

    const theftRisk = `theft_risk=${this.theftRisk}`;

    const params = [cancelDateFrom, cancelDateTo, locations, theftRisk].filter(Boolean);
    return params.join('&');
  }

  handleDateFromChange() {
    this.highlightApply = true;
  }

  handleDateToChange() {
    this.highlightApply = true;
  }

  clearFilterFromDate() {
    this.dateFrom.set(undefined);
    this.highlightApply = true;
  }

  clearFilterToDate() {
    this.dateTo.set(undefined);
    this.highlightApply = true;
  }

  resetFilters() {
    this.dateFrom.set(undefined);
    this.dateTo.set(undefined);
    this.selectedLocations.set([]);
    this.highlightApply = true;
  }
}
